import { useEffect, useRef, useState } from "react";
import { AppState, AppStateStatus } from "react-native";

const MS_IN_SECOND = 1000;
const DEFAULT_REFRESH_INTERVAL_MINUTES = 1;

/**
 * Hook that provides a current time value that updates periodically while the app is active.
 * This is used to trigger re-computation of time-based states without refetching data from the server.
 *
 * The hook automatically:
 * - Updates current time periodically when app is active (default: every minute)
 * - Pauses updates when app goes to background
 * - Resumes updates when app returns to foreground
 *
 * @param intervalMinutes - Update interval in minutes (default: 1 minute)
 * @returns Current Date object that updates periodically
 */
export function usePeriodicRefresh(intervalMinutes: number = DEFAULT_REFRESH_INTERVAL_MINUTES): Date {
  const [currentTime, setCurrentTime] = useState(() => new Date());
  const appState = useRef<AppStateStatus>(AppState.currentState);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const refreshIntervalMs = intervalMinutes * 60 * MS_IN_SECOND;

    const startPeriodicUpdate = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      intervalRef.current = setInterval(() => {
        setCurrentTime(new Date());
      }, refreshIntervalMs);
    };

    const stopPeriodicUpdate = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    // Handle app state changes
    const subscription = AppState.addEventListener("change", (nextAppState: AppStateStatus) => {
      const wasActive = appState.current === "active";
      const isActive = nextAppState === "active";

      // App became active - start periodic update
      if (!wasActive && isActive) {
        startPeriodicUpdate();
      }
      // App went to background/inactive - stop periodic update
      else if (wasActive && !isActive) {
        stopPeriodicUpdate();
      }

      appState.current = nextAppState;
    });

    // Start periodic update if app is currently active
    if (appState.current === "active") {
      startPeriodicUpdate();
    }

    // Cleanup on unmount
    return () => {
      subscription.remove();
      stopPeriodicUpdate();
    };
  }, [intervalMinutes]);

  return currentTime;
}
